import {
  PhotoSquareIcon,
  PhotoSquareOutlineIcon,
  ShutterIcon,
  WarningColoredIcon,
  WarningIcon,
} from '@carboncopyco/icons';
import {
  FrameType,
  type Tour,
  type TourFrame,
  type TourStep,
} from '@carboncopyco/types';
import {
  Badge,
  ColorHex,
  ContentEditableInput,
  Loading,
  Pill,
  TextBase,
  Tooltip,
  TooltipBubble,
  TooltipMessage,
} from '@carboncopyco/ui';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTourSelector } from '../../../../../hooks';
import {
  selectActiveFrameId,
  selectActiveStepId,
  selectAddingStepInProgress,
  selectFrameWarningsCount,
} from '../../../../../store';
import { useTourEditorPageProps } from '../../../TourEditorPageContext';
import type { ReplayerEditorRef } from '../../../ToursReplayerEditor';
import { frameHasStepWithJumpToStepTargetButton } from '../../../helpers';
import { SortableItem } from '../SortableItem';
import { SortableList } from '../SortableList';
import { AddStep } from './AddStep';
import ContextMenu from './ContextMenu';
import { Step } from './Step';
import useFrameActions from './useFrameActions';

interface IFrame {
  frame: TourFrame;
  frames: TourFrame[];
  selected: boolean;
  fileServiceApiToken: Tour['fileServiceToken'];
  start?: boolean;
  replayerRef: React.RefObject<ReplayerEditorRef>;
  isGeneratingReplayFrameChunks: boolean;
  activeFramesLength: number;
  doReplay: (step: TourStep) => void;
}

export const Frame = ({
  frame,
  frames,
  selected,
  fileServiceApiToken,
  isGeneratingReplayFrameChunks,
  replayerRef,
  activeFramesLength,
  doReplay,
}: IFrame) => {
  const { uuid } = useTourEditorPageProps();
  const activeStepId = useSelector(selectActiveStepId(uuid));
  const [isFrameEditMode, setIsFrameEditMode] = useState<boolean>(false);
  const isAddingStepInProgress = useSelector(selectAddingStepInProgress(uuid));
  const activeFrameId = useTourSelector(selectActiveFrameId(uuid));
  const warningTooltipRef = useRef();

  const frameWarningsCount = useTourSelector(
    selectFrameWarningsCount(uuid, frame.id)
  );

  const {
    id,
    isActive: isFrameActive,
    frameTitle: title,
    steps,
    tourData: { id: tourId },
    frameType,
  } = frame;

  const isNewFrame = frame?.copiedFrom && frame?.isDraft;
  const isRecordingFrame = frame.frameType === FrameType.recording;

  const {
    handleFrameTitleUpdate,
    handleOnFrameClick,
    handleToggleActive,
    handleDeleteFrame,
    handleConvertToFrame,
    handleStepsUpdate,
    isDeleteLoading,
  } = useFrameActions({
    tourId,
    frame,
    frames,
    isFrameActive,
    activeFrameId,
    activeStepId,
    replayerRef: replayerRef,
    frameType: frame.frameType,
    steps,
    onFrameClick: (isEditMode) => setIsFrameEditMode(isEditMode),
    doReplay,
    fileServiceApiToken,
  });

  /**
   * When converting frames from recording to screenshot and vice versa, we need to replay current frame
   * in order to update view
   */
  useEffect(() => {
    if (!selected) {
      return;
    }

    replayerRef.current.playFrame(frame);
    setTimeout(() => {
      replayerRef.current.refreshTooltip();
    }, 500);
  }, [frameType]);

  const stepPillLabel = useMemo(
    () =>
      steps.length === 1
        ? '1 step'
        : steps.length > 1
          ? `${steps.length} steps`
          : 'No steps',

    [steps]
  );

  const stepsWidthId = useMemo(
    () =>
      steps.map((step) => ({
        ...step,
        id: step.stepId,
      })),
    [steps]
  );

  const frameHasStepWithJump: boolean = useMemo(() => {
    return frames
      .filter((frameItem) => !frameItem.isDisabled)
      .flatMap((frameItem) => frameItem.steps)
      .some((step) => {
        return frameHasStepWithJumpToStepTargetButton(frame, step);
      });
  }, [frames, frame]);

  const warningTooltipMessage = useMemo(() => {
    return `Resolve ${frameWarningsCount} ${
      frameWarningsCount > 1 ? 'issues' : 'issue'
    } on this frame`;
  }, [frameWarningsCount]);

  const renderContextMenu = useMemo(() => {
    if (isDeleteLoading) {
      return (
        <div className="pr-2.5">
          <Loading color="text-ds-primary-300" />
        </div>
      );
    }

    return (
      <ContextMenu
        isFrameActive={isFrameActive}
        frameType={frame.frameType}
        onDeleteClick={handleDeleteFrame}
        onVisibilityClick={handleToggleActive}
        onConvertToFrameClick={handleConvertToFrame}
        frameHasStepWithJump={frameHasStepWithJump}
        activeFramesLength={activeFramesLength}
        isFrameConvertable={!!frame.frameDataUrl && !!frame.frameScreenshotUrl}
      />
    );
  }, [
    isFrameActive,
    isDeleteLoading,
    activeFramesLength,
    handleDeleteFrame,
    handleToggleActive,
  ]);

  return (
    <div
      role="listitem"
      data-testid="drawer-frame-item"
      className={classNames(
        'w-full border border-ds-natural-150 rounded-md py-5 px-4 hover:border-ds-natural-300 outline-none',
        isFrameActive ? 'bg-white' : 'bg-ds-natural-50 border-ds-natural-150',
        isGeneratingReplayFrameChunks && 'opacity-50',
        {
          'shadow-ds-sm drop-shadow-[0_0_1.5px_#C9C8D0]': selected,
          'bg-ds-natural-50 border-ds-natural-150 ': !isFrameActive,
        }
      )}
      onClick={(event) => {
        handleOnFrameClick(event, id, selected, steps[0]);
      }}
    >
      <AnimatePresence>
        <motion.div
          key="main"
          className={classNames('w-full text-ds-natural-200 text-sm', {
            'text-ds-natural-600': isFrameActive,
          })}
        >
          <div className="flex justify-between items-center">
            <div className="w-full">
              <div
                className={classNames(
                  'text-xs text-ds-natural-200 flex items-center gap-1',
                  {
                    'text-ds-natural-300': isFrameActive,
                  }
                )}
              >
                <TooltipBubble
                  placement="top"
                  title={isRecordingFrame ? 'HTML frame' : 'Image frame'}
                >
                  {isRecordingFrame ? (
                    <ShutterIcon
                      size={12}
                      color={
                        selected ? ColorHex.primary300 : ColorHex.natural300
                      }
                    />
                  ) : selected ? (
                    <PhotoSquareIcon
                      size={12}
                      color={ColorHex.primary300}
                    />
                  ) : (
                    <PhotoSquareOutlineIcon
                      size={12}
                      color={ColorHex.natural300}
                    />
                  )}
                </TooltipBubble>
                <TextBase
                  label="Frame"
                  color={
                    selected ? 'text-ds-primary-300' : 'text-ds-natural-300'
                  }
                />
                <Pill
                  label={`${stepPillLabel}`}
                  variant={
                    steps.length > 0
                      ? isFrameActive
                        ? 'grey'
                        : 'disabled'
                      : 'yellow'
                  }
                  leftIcon={!steps.length ? <WarningIcon /> : null}
                />
                {frameWarningsCount > 0 && (
                  <>
                    <div ref={warningTooltipRef}>
                      <Pill
                        label={`${frameWarningsCount}`}
                        variant="yellow"
                        leftIcon={<WarningColoredIcon />}
                      />
                    </div>

                    <Tooltip
                      placement="top"
                      anchorRef={warningTooltipRef}
                      noArrow
                    >
                      <TooltipMessage title={warningTooltipMessage} />
                    </Tooltip>
                  </>
                )}
                {isNewFrame && (
                  <Badge
                    className="ml-1"
                    label="new"
                    variant="dark"
                  />
                )}
              </div>
              {isFrameActive ? (
                <ContentEditableInput
                  inputSize="sm"
                  onChange={async (newFrameTitle: string) =>
                    await handleFrameTitleUpdate(newFrameTitle, id)
                  }
                  bordered
                >
                  {title}
                </ContentEditableInput>
              ) : (
                title
              )}
            </div>
            {renderContextMenu}
          </div>
        </motion.div>
        {selected && (
          <motion.div
            key="expanded"
            className="py-[9px] flex flex-col mt-2"
            exit={{ display: 'none' }}
          >
            <SortableList
              items={stepsWidthId}
              displayedItems={stepsWidthId}
              onItemOrderUpdate={(items) => {
                const stepsWithoutId = items.map((item) => {
                  const { id, ...step } = item;
                  return step;
                });
                handleStepsUpdate(stepsWithoutId);
              }}
              renderItem={(stepWithId) => {
                const { id, ...step } = stepWithId;

                const isStepSelected =
                  !isFrameEditMode &&
                  !isAddingStepInProgress &&
                  activeStepId === step.stepId;

                return (
                  <SortableItem
                    id={step.stepId}
                    key={step.stepId}
                    disableDragging={stepsWidthId.length === 1}
                  >
                    <Step
                      key={step.stepId}
                      tourId={tourId}
                      step={step}
                      frame={frame}
                      frames={frames}
                      replayerRef={replayerRef}
                      doReplay={doReplay}
                      selected={isStepSelected}
                      isStepDisabled={!isFrameActive}
                      onStepSelect={() => setIsFrameEditMode(false)}
                    />
                  </SortableItem>
                );
              }}
            />
            <AddStep
              isAddingStep={isAddingStepInProgress}
              isStepDisabled={!isFrameActive}
              frame={frame}
              replayerRef={replayerRef}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
